import { useState } from "react";
import { Send, CheckCircle2 } from "lucide-react";
import Reveal from "./Reveal";
import { useSite } from "@/context/SiteContext";
import { api, formatApiErrorDetail, track } from "@/lib/api";

export default function ContactSection() {
  const { openBooking } = useSite();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    setError("");
    if (!form.name || !form.email || !form.message) { setError("Please fill in your name, email, and message."); return; }
    setBusy(true);
    try {
      await api.post("/support", form);
      track("support_message_sent");
      setDone(true);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      className="section-pad relative overflow-hidden text-white"
      style={{ backgroundImage: "url(/unified-cta-bg.png)", backgroundSize: "cover", backgroundPosition: "center" }}
      data-testid="contact-section"
    >
      <div className="relative max-w-[1400px] mx-auto px-5 lg:px-10 grid lg:grid-cols-2 gap-12 items-center">
        <Reveal direction="left">
          <div className="eyebrow text-[#00CFFF] mb-4">Support & questions</div>
          <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-[42px] tracking-tight leading-[1.12]">
            Need a hand? <span className="text-gradient">Talk to our team.</span>
          </h2>
          <p className="mt-4 text-slate-300 text-base sm:text-lg leading-relaxed max-w-lg">
            Questions about our specialists, pricing, or how it all works? Send us a message — our team reads every one and replies by email.
          </p>
          <button className="btn btn-outline-light mt-8" onClick={() => openBooking(null, "booking")}>
            Or book a free consultation instead
          </button>
        </Reveal>

        <Reveal direction="right">
          <div className="relative rounded-3xl p-[1.5px] overflow-hidden" style={{ boxShadow: "0 0 44px rgba(0,191,255,0.22), 0 24px 60px rgba(0,0,0,0.4)" }} data-testid="contact-form-card">
            <div className="card-shimmer" aria-hidden="true" />
            <div className="relative rounded-[22px] bg-[rgba(9,18,36,0.62)] backdrop-blur-xl border border-[rgba(0,207,255,0.45)] p-7 sm:p-8">
            {done ? (
              <div className="text-center py-10" data-testid="contact-success">
                <span className="w-14 h-14 rounded-full bg-emerald-400/15 flex items-center justify-center mx-auto">
                  <CheckCircle2 size={28} className="text-emerald-400" />
                </span>
                <h3 className="mt-5 font-display font-extrabold text-xl">Message received!</h3>
                <p className="mt-2 text-sm text-slate-300">We'll get back to you at {form.email} as soon as possible.</p>
                <button className="btn btn-outline-light mt-6" onClick={() => { setDone(false); setForm({ name: "", email: "", subject: "", message: "" }); }}>
                  Send another message
                </button>
              </div>
            ) : (
              <>
                <h3 className="font-display font-extrabold text-lg mb-5">Send us a message</h3>
                <div className="space-y-3">
                  <div className="grid sm:grid-cols-2 gap-3">
                    <input className="glass-input" placeholder="Your name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-input-name" />
                    <input className="glass-input" type="email" placeholder="Email *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-input-email" />
                  </div>
                  <input className="glass-input" placeholder="Subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="contact-input-subject" />
                  <textarea className="glass-input" rows={4} placeholder="How can we help? *" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-input-message" />
                </div>
                {error && <div className="mt-3 text-sm text-red-400" data-testid="contact-error">{error}</div>}
                <button className="btn btn-primary w-full mt-5" onClick={submit} disabled={busy} data-testid="contact-submit-button">
                  {busy ? "Sending..." : "Send Message"} <Send size={15} />
                </button>
              </>
            )}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
